import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout';
import UserLayout from '@/components/Layout/UserLayout';
import { Button, Callout, Card, FormCheckbox, FormInput, FormSelect, TradingStrategySelect } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import SearchSelect, { type SearchSelectItem } from '@/components/UI/SearchSelect';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useTradingStrategies } from '@/hooks/useTradingStrategies';
import { parsePositiveInt, type EntryOptimizerPreflight } from './entryOptimizerUtils';

type RangeDraft = Record<string, { min: string; max: string }>;

/**
 * Study wizard: pick a strategy (its attached Trade Logic V2 entry version is the
 * sweep source), review/adjust the sweepable tunables, pick a completed experiment
 * whose universe and window the envelope clones, then launch the envelope run.
 */
const EntryOptimizerNew = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [searchParams] = useSearchParams();

    const [strategyId, setStrategyId] = useState<number | null>(parsePositiveInt(searchParams.get('strategyId')));

    // Deep links from ExperimentResults preselect that experiment as the universe source.
    const [sourceExperimentId, setSourceExperimentId] = useState(() => {
        const fromQuery = parsePositiveInt(searchParams.get('sourceExperimentId'));

        return fromQuery !== null ? String(fromQuery) : '';
    });

    const [rangeDraft, setRangeDraft] = useState<RangeDraft>({});
    // '' = the strategy's attached entry version; otherwise an entry definition id
    // whose LATEST version becomes the sweep source.
    const [sourceDefinitionChoice, setSourceDefinitionChoice] = useState('');

    const { strategies } = useTradingStrategies();
    const strategy = useMemo(() => strategies.find((candidate) => candidate.id === strategyId) ?? null, [strategies, strategyId]);

    const attachedVersionId = strategy?.tradeLogicV2EntryVersionId ?? null;
    const chosenDefinitionId = parsePositiveInt(sourceDefinitionChoice);

    const entryDefinitionsQuery = useAppQuery({
        ...trpc.strategy.fetchTradeLogicDefinitions.queryOptions({ kind: 'entry' }),
        enabled: strategyId !== null,
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    const definitionVersionsQuery = useAppQuery({
        ...trpc.strategy.fetchTradeLogicDefinitionVersions.queryOptions({ definitionId: chosenDefinitionId ?? 0 }),
        enabled: chosenDefinitionId !== null,
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    const chosenDefinitionLatestVersionId = useMemo(() => {
        const versions: { versionId: number; version: number }[] = definitionVersionsQuery.data?.versions ?? [];

        let latest: { versionId: number; version: number } | null = null;

        for (const candidate of versions) {
            if (latest === null || candidate.version > latest.version) latest = candidate;
        }

        return latest?.versionId ?? null;
    }, [definitionVersionsQuery.data?.versions]);

    const sourceVersionId = chosenDefinitionId !== null ? chosenDefinitionLatestVersionId : attachedVersionId;

    const preflightQuery = useAppQuery({
        ...trpc.entryOptimizer.preflight.queryOptions({
            sourceVersionId: sourceVersionId ?? 0,
            // Ranks discovered gates by the strategy's Parameter Discovery priors.
            ...(strategyId !== null ? { strategyId } : {})
        }),
        enabled: sourceVersionId !== null,
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    const preflight = preflightQuery.data?.preflight ?? null;

    const sourceDefinitionOptions = useMemo(
        () => [
            {
                label:
                    attachedVersionId !== null
                        ? `Strategy's attached entry (version #${attachedVersionId})`
                        : 'Strategy has no attached entry',
                value: ''
            },
            ...(entryDefinitionsQuery.data?.definitions ?? []).map((definition) => ({
                label: `${definition.name} (definition #${definition.definitionId})`,
                value: String(definition.definitionId)
            }))
        ],
        [attachedVersionId, entryDefinitionsQuery.data?.definitions]
    );

    const experimentsQuery = useAppQuery({
        ...trpc.experiment.list.queryOptions({ executionStatus: 'completed', limit: 100, offset: 0 }),
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    const chosenExperiment = useMemo(
        () => (experimentsQuery.data?.experiments ?? []).find((experiment) => String(experiment.id) === sourceExperimentId) ?? null,
        [experimentsQuery.data?.experiments, sourceExperimentId]
    );

    const experimentOptions = useMemo<SearchSelectItem[]>(
        () =>
            (experimentsQuery.data?.experiments ?? [])
                .filter((experiment) => experiment.strategyId === strategyId)
                .map((experiment) => ({
                    label: experiment.name,
                    meta: `${experiment.entityCount} entities`,
                    name: experiment.name,
                    value: experiment.id
                })),
        [experimentsQuery.data?.experiments, strategyId]
    );

    const createEnvelopeRunMutation = useMutation({
        ...trpc.entryOptimizer.createEnvelopeRun.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async (result) => {
            const droppedCount = result.run.droppedEntities?.length ?? 0;

            showToast(
                `Tuning started — collecting entry data now${
                    droppedCount ? ` (${droppedCount} entit${droppedCount === 1 ? 'y' : 'ies'} skipped, no longer tradable)` : ''
                }`,
                'success',
                droppedCount > 0
            );

            // Mark the home page's lists stale so the new run shows immediately,
            // not on the next poll.
            await queryClient.invalidateQueries(trpc.entryOptimizer.listEnvelopeRuns.pathFilter());
            void navigate('/user/entryOptimizer');
        }
    });

    // Discovered gate exclusions: candidates are opt-out (all included by default).
    const [excludedDiscoveredIds, setExcludedDiscoveredIds] = useState<Record<string, boolean>>({});

    const includedDiscovered = useMemo(
        () => (preflight?.discovered ?? []).filter(({ tunable }) => !excludedDiscoveredIds[tunable.tunableId]),
        [preflight?.discovered, excludedDiscoveredIds]
    );

    // rangeDraft holds only the user's edits; defaults come straight from preflight.
    const sweepRangeOverrides = useMemo(() => {
        const overrides: Record<string, { min: number; max: number }> = {};

        for (const { tunable, range } of [...(preflight?.sweepable ?? []), ...includedDiscovered]) {
            const draft = rangeDraft[tunable.tunableId];
            const min = draft?.min !== undefined && draft.min !== '' ? Number(draft.min) : range.min;
            const max = draft?.max !== undefined && draft.max !== '' ? Number(draft.max) : range.max;

            if (!Number.isNaN(min) && !Number.isNaN(max)) overrides[tunable.tunableId] = { min, max };
        }

        return overrides;
    }, [preflight, rangeDraft, includedDiscovered]);

    const canLaunch =
        strategyId !== null &&
        sourceVersionId !== null &&
        sourceExperimentId !== '' &&
        ((preflight?.sweepable.length ?? 0) > 0 || includedDiscovered.length > 0) &&
        !createEnvelopeRunMutation.isPending;

    const launch = () => {
        if (strategyId === null || sourceVersionId === null || sourceExperimentId === '') return;

        createEnvelopeRunMutation.mutate({
            strategyId,
            sourceVersionId,
            sourceExperimentId: Number.parseInt(sourceExperimentId, 10),
            sweepRangeOverrides,
            discoveredTunableIds: includedDiscovered.map(({ tunable }) => tunable.tunableId)
        });
    };

    return (
        <UserLayout containerSize="1100px" title="Tune an Entry - Project 314">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold text-zinc-100">Tune an Entry</h1>
                <p className="mt-1 text-sm text-zinc-400">
                    Pick a strategy and we&apos;ll test alternative settings for its entry logic — then tell you which ones beat what you
                    have now.
                </p>
            </div>

            <Card
                subtitle="Defaults to the strategy's live entry logic, but any of your entry definitions works."
                title="1. Strategy & entry logic"
            >
                <TradingStrategySelect label="Strategy" value={strategyId} onChange={setStrategyId} />

                {strategyId !== null && (
                    <FormSelect
                        helpText="Which entry logic to tune. Draft entries with numeric settings appear here too."
                        label="Entry logic to tune"
                        options={sourceDefinitionOptions}
                        value={sourceDefinitionChoice}
                        onChange={(event) => setSourceDefinitionChoice(event.target.value)}
                    />
                )}

                {strategy && sourceDefinitionChoice === '' && attachedVersionId === null && (
                    <Callout className="mt-3" variant="warning">
                        Strategy #{strategy.id} has no Trade Logic V2 entry version attached. Pick one of your entry definitions above, or
                        attach an entry in the Trade Logic editor first.
                    </Callout>
                )}

                {sourceVersionId !== null && preflight && (
                    <div className="mt-3 text-sm text-zinc-400">
                        Tuning: <span className="text-zinc-200">{preflight.definitionName}</span>
                    </div>
                )}
            </Card>

            {sourceVersionId !== null && (
                <Card className="mt-4" subtitle="The settings we'll test, and the range we'll try for each." title="2. Settings to test">
                    {preflightQuery.isLoading ? (
                        <DataLoadingSpinner text="Reading the entry's settings..." />
                    ) : preflightQuery.error instanceof Error ? (
                        <Callout variant="error">{preflightQuery.error.message}</Callout>
                    ) : preflight ? (
                        <>
                            <TunablesEditor preflight={preflight} rangeDraft={rangeDraft} setRangeDraft={setRangeDraft} />

                            {(preflight.discovered?.length ?? 0) > 0 && (
                                <div className="mt-4 border-t border-zinc-800 pt-4">
                                    <div className="mb-1 text-sm font-medium text-zinc-200">Suggested new filters</div>
                                    <p className="mb-3 text-xs text-zinc-500">
                                        Extra conditions this entry doesn&apos;t use yet, but could. We&apos;ll test them alongside your
                                        settings — your entry isn&apos;t changed unless you later apply a winner.
                                    </p>
                                    <div className="flex flex-col gap-2">
                                        {preflight.discovered.map(({ tunable, range, prior }) => (
                                            <div
                                                className="grid grid-cols-1 items-center gap-3 rounded-md border border-zinc-800 bg-black/20 p-3 md:grid-cols-[24px_1fr_120px_120px]"
                                                key={tunable.tunableId}
                                            >
                                                <FormCheckbox
                                                    checked={!excludedDiscoveredIds[tunable.tunableId]}
                                                    onChange={(event) =>
                                                        setExcludedDiscoveredIds((previous) => ({
                                                            ...previous,
                                                            [tunable.tunableId]: !event.target.checked
                                                        }))
                                                    }
                                                />
                                                <div className="min-w-0">
                                                    <div className="truncate text-sm font-medium text-zinc-100">{tunable.label}</div>
                                                    <div className="mt-0.5 truncate text-xs text-zinc-500">{tunable.featureLabel}</div>
                                                    {prior && (
                                                        <div className="mt-0.5 truncate text-xs text-zinc-600">
                                                            Past signal: {prior.status}
                                                            {prior.lcbScore !== null ? ` (${Number(prior.lcbScore).toFixed(3)})` : ''}
                                                        </div>
                                                    )}
                                                </div>
                                                <FormInput
                                                    noMargin
                                                    label="Sweep min"
                                                    value={rangeDraft[tunable.tunableId]?.min ?? String(range.min)}
                                                    onChange={(event) =>
                                                        setRangeDraft((previous) => ({
                                                            ...previous,
                                                            [tunable.tunableId]: {
                                                                ...(previous[tunable.tunableId] ?? { min: '', max: '' }),
                                                                min: event.target.value
                                                            }
                                                        }))
                                                    }
                                                />
                                                <FormInput
                                                    noMargin
                                                    label="Sweep max"
                                                    value={rangeDraft[tunable.tunableId]?.max ?? String(range.max)}
                                                    onChange={(event) =>
                                                        setRangeDraft((previous) => ({
                                                            ...previous,
                                                            [tunable.tunableId]: {
                                                                ...(previous[tunable.tunableId] ?? { min: '', max: '' }),
                                                                max: event.target.value
                                                            }
                                                        }))
                                                    }
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    {(preflight.discoveredSkipped?.length ?? 0) > 0 && (
                                        <p className="mt-2 text-xs text-zinc-600">
                                            {preflight.discoveredSkipped.length} nearby condition
                                            {preflight.discoveredSkipped.length === 1 ? ' was' : 's were'} skipped:{' '}
                                            {preflight.discoveredSkipped
                                                .slice(0, 4)
                                                .map((skip) => `${skip.featureKey.split('.').slice(-2).join('.')} (${skip.reason})`)
                                                .join('; ')}
                                            {preflight.discoveredSkipped.length > 4 ? '; …' : ''}
                                        </p>
                                    )}
                                </div>
                            )}
                        </>
                    ) : null}
                </Card>
            )}

            {sourceVersionId !== null && (
                <Card
                    className="mt-4"
                    subtitle="We reuse the entities and date window from one of your past experiments."
                    title="3. Which entities to test on"
                >
                    <SearchSelect
                        helpText="Completed experiments for the selected strategy."
                        items={experimentOptions}
                        label="Test universe"
                        mode="single"
                        placeholder="Select an experiment…"
                        value={sourceExperimentId ? Number(sourceExperimentId) : null}
                        onChange={(value) => setSourceExperimentId(value !== null ? String(value) : '')}
                    />
                    {experimentOptions.length === 0 && !experimentsQuery.isLoading && (
                        <Callout className="mt-3" variant="info">
                            No completed experiments found for this strategy. Run one first — tuning reuses its proven set of entities and
                            dates.
                        </Callout>
                    )}
                    {chosenExperiment && (
                        <p className="mt-3 text-xs text-zinc-500">
                            How long this takes: we collect entry data across {chosenExperiment.entityCount} entities first (a few hours on
                            the queue, scaling with the number of entities), then the tuning itself takes seconds.
                        </p>
                    )}
                </Card>
            )}

            <div className="mt-6 flex justify-end gap-2">
                <Button to="/user/entryOptimizer" variant="outline-dark">
                    Cancel
                </Button>
                <Button disabled={!canLaunch} onClick={launch}>
                    {createEnvelopeRunMutation.isPending ? 'Starting…' : 'Start Tuning'}
                </Button>
            </div>
        </UserLayout>
    );
};

const TunablesEditor = ({
    preflight,
    rangeDraft,
    setRangeDraft
}: {
    preflight: EntryOptimizerPreflight;
    rangeDraft: RangeDraft;
    setRangeDraft: (updater: (previous: RangeDraft) => RangeDraft) => void;
}) => {
    const updateRange = (tunableId: string, field: 'max' | 'min', value: string) => {
        setRangeDraft((previous) => ({
            ...previous,
            [tunableId]: { ...(previous[tunableId] ?? { min: '', max: '' }), [field]: value }
        }));
    };

    return (
        <div className="flex flex-col gap-4">
            {preflight.sweepable.length === 0 ? (
                <Callout variant="warning">
                    This entry has no numeric settings to tune — its conditions compare states or labels rather than numbers. Tuning works
                    on numeric comparisons (e.g. RSI below 30, or “within 20 bars of a crossover”). Pick a different entry above, or add a
                    numeric condition in the Trade Logic editor and tune that version. You can still try the suggested new filters below.
                </Callout>
            ) : (
                <div className="flex flex-col gap-2">
                    {preflight.sweepable.map(({ tunable, range }) => (
                        <div
                            className="grid grid-cols-1 items-center gap-3 rounded-md border border-zinc-800 bg-black/20 p-3 md:grid-cols-[1fr_120px_120px_120px]"
                            key={tunable.tunableId}
                        >
                            <div className="min-w-0">
                                <div className="truncate text-sm font-medium text-zinc-100">
                                    {tunable.featureLabel ?? tunable.tunableId}
                                </div>
                                <div className="mt-0.5 text-xs text-zinc-500">{tunable.operator}</div>
                            </div>
                            <div className="text-sm text-zinc-300">
                                Now:{' '}
                                <span className="text-zinc-100">
                                    {Array.isArray(tunable.incumbentValue)
                                        ? `[${tunable.incumbentValue.join(', ')}]`
                                        : tunable.incumbentValue}
                                </span>
                            </div>
                            <FormInput
                                noMargin
                                label="Try from"
                                value={rangeDraft[tunable.tunableId]?.min ?? String(range.min)}
                                onChange={(event) => updateRange(tunable.tunableId, 'min', event.target.value)}
                            />
                            <FormInput
                                noMargin
                                label="Try to"
                                value={rangeDraft[tunable.tunableId]?.max ?? String(range.max)}
                                onChange={(event) => updateRange(tunable.tunableId, 'max', event.target.value)}
                            />
                        </div>
                    ))}
                </div>
            )}

            {preflight.skipped.length > 0 && (
                <div>
                    <div className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">Can&apos;t be tuned</div>
                    <div className="flex flex-col gap-1">
                        {preflight.skipped.map((skipped) => (
                            <div className="text-xs text-zinc-500" key={skipped.tunableId}>
                                <span className="text-zinc-400">{skipped.label}</span> — {skipped.reason}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default EntryOptimizerNew;
